import {
  BET_PILL_WIDTH,
  DEALER_SEAT_OFFSET_PX,
  FLANK_BET_GAP_PX,
  SEAT_CARD_WIDTH,
  SEAT_CONTAINER_HEIGHT,
  SMALL_CARD_WIDTH
} from 'constants/gameConstants';
import { PORTRAIT_FLANK_BET_COLUMNS_PCT, SeatPosition } from 'hooks/useEllipticalPositions';

import { seatEdgeInsets } from 'components/Seat/modes/seatEdgeInsets';
import {
  getCardOffset,
  getShowdownCardScale,
  seatCardBandReach
} from 'components/Seat/SeatWithPlayer/SeatCards';

/**
 * How large the seats may be painted before they start covering each other.
 *
 * THIS IS THE "CARDS SHRINK, THEY NEVER GET COVERED" RULE.
 *
 * The table's two halves have always disagreed. Seat POSITIONS are percentages
 * of the container, so they hold their arrangement at any size. Seat CONTENTS
 * are design pixels multiplied by one uniform scale, so they do not: raise the
 * scale and every seat grows while the gaps between them stay exactly where they
 * were, in percentage terms. Past some scale the furniture is simply wider than
 * the space the ring left for it, and something lands on a card.
 *
 * That is why Table View Size was capped at +20% with the comment "so even the
 * 'Largest' setting can't cause element overlap". The cap was the only defence
 * available, and it worked by keeping every player's cards small — while still
 * not actually working (CX reproduced a blocked flop card at MAXIMUM size on
 * 2026-09-10).
 *
 * The defence here is the other way round: work out what the ring can afford and
 * clamp to that. A player asks for Largest and gets Largest wherever there is
 * room for it — a single table on a 27" monitor — and gets as much of it as fits
 * on a 4-table grid or a phone. The setting stops being a promise the layout
 * cannot keep.
 *
 * WHAT IS AND IS NOT MODELLED. This is a pairwise axis-aligned test between seat
 * FOOTPRINTS, which is the same footprint `seatEdgeInsets` already uses to keep
 * a seat inside the viewport — reused rather than restated, because those two
 * disagreeing is a bug that has already happened once here. It bounds seat
 * against seat. It does not model transient overlays (winner banner, uncalled
 * bet) or page chrome; those are handled where they are rendered.
 */

/**
 * Hold'em and Omaha — the hand sizes a seat can hold or reveal.
 *
 * Declared before its users: both extents read it at call time, but a `const`
 * that appears below the functions that close over it is a temporal-dead-zone
 * trap waiting for the first module-load-time caller.
 */
const SHOWDOWN_HAND_SIZES = [2, 4];

/**
 * Horizontal extent of a seat's painted footprint, left and right of its anchor.
 *
 * Starts from `seatEdgeInsets` — the canonical footprint, reused rather than
 * restated — and widens it to cover the SHOWDOWN spread as well.
 *
 * That widening matters and is easy to miss. `seatEdgeInsets` describes the
 * FACE-DOWN hand, which fans left and reaches ~56px that way and nothing to the
 * right, so its right bound is the seat card alone (40px). A revealed 4-card
 * Omaha hand is a centred spread, ~45px BOTH ways — 5px wider on the right than
 * the seat card it is measured against. Sizing the separation clamp off the
 * face-down hand alone would therefore let two seats sit exactly close enough to
 * collide at the moment the cards flip, which is precisely the reported bug
 * ("showdown hands stack on each other so opponents' cards can't be read").
 *
 * The viewport clamp deliberately keeps its own narrower bound — changing it
 * would move every seat's edge inset and is EN-10726's territory, not this
 * ticket's.
 */
const seatHorizontalExtent = (isPortrait: boolean) => {
  const insets = seatEdgeInsets(isPortrait);
  let faceUpLeft = 0;
  let faceUpRight = 0;
  for (const cardCount of SHOWDOWN_HAND_SIZES) {
    const scale = getShowdownCardScale(cardCount);
    const width = SMALL_CARD_WIDTH * scale;
    for (let index = 0; index < cardCount; index++) {
      const { x } = getCardOffset(index, false, false, false, isPortrait, cardCount);
      faceUpLeft = Math.max(faceUpLeft, -x);
      faceUpRight = Math.max(faceUpRight, x + width);
    }
  }
  // A seat's own BET PILL is part of its footprint too. It is placed in the gap
  // between the seat and the felt centre and is clamped against that seat's own
  // furniture (`dealerPlusBetX`), so it moves further out as the furniture
  // grows — which means at a large enough scale it reaches the NEIGHBOUR.
  //
  // Counted on both sides rather than only inboard: which side is inboard flips
  // across the felt's midline, so a symmetric allowance is the one that holds
  // for every seat without the clamp needing to know where each seat sits.
  return {
    left: Math.max(insets.left, faceUpLeft),
    right: Math.max(insets.right, faceUpRight)
  };
};

/**
 * How far apart two seats' anchors must be horizontally, at scale 1.
 *
 * TWO separate requirements, and the larger wins:
 *
 *  1. CARDS AND FURNITURE — one seat's right extent plus the other's left.
 *  2. THE BET PILL — which is the binding one, and is not obvious. A flank
 *     seat's pill is pushed inboard by `dealerPlusBetX` (see
 *     useEllipticalPositions' seatClearancePct), a distance that scales with the
 *     furniture. So the gap has to hold the pill's whole travel, plus half the
 *     pill, plus the neighbour's own card edge it must not reach.
 *
 * Derived from the same constants `dealerPlusBetX` is built from rather than
 * allowed for with a margin. An earlier version added half a pill to BOTH sides
 * of the footprint, which came out close to right by accident and cost ~7% of
 * the permitted scale on rings where the card requirement was already the
 * binding one.
 */
const horizontalGapNeeded = (
  { left, right }: { left: number; right: number },
  isPortrait: boolean
): number => {
  const cardsAndFurniture = left + right;
  // The two orientations place their flank bets by DIFFERENT rules, so only one
  // of them is a seat-to-seat distance at all:
  //
  //  - LANDSCAPE puts the pill at an offset from its own seat that clears the
  //    dealer marker outright (`dealerPlusBetX`), and that offset scales with
  //    the furniture — so it IS a pairwise distance, charged below.
  //  - PORTRAIT sends it to a fixed COLUMN of felt (`PORTRAIT_FLANK_BET`), which
  //    does not move with the seat at all. Charging the landscape distance here
  //    cost ~16% of the permitted scale on every portrait ring — which is every
  //    multi-table grid cell — while still being the wrong shape of constraint.
  //    `maxSeatScaleForFlankColumns` bounds those directly instead.
  if (isPortrait) return cardsAndFurniture;

  const pillTravel =
    SEAT_CARD_WIDTH / 2 - DEALER_SEAT_OFFSET_PX + BET_PILL_WIDTH / 2 + FLANK_BET_GAP_PX;
  const pillReachesNeighbour = pillTravel + BET_PILL_WIDTH / 2 + SEAT_CARD_WIDTH / 2;
  return Math.max(cardsAndFurniture, pillReachesNeighbour);
};

/**
 * Vertical extent of a seat's painted footprint above and below its anchor.
 *
 * Takes the TALLEST hand, face-down or face-up, for the same reason
 * `seatHorizontalExtent` takes the widest: a revealed hand reaches further than
 * a concealed one. A face-down landscape card paints at 0.6 while a two-card
 * reveal paints at SCALE_FACE_UP 0.75, so modelling the face-down band alone
 * understates the reach by ~9.7px per seat — and this axis is the BINDING one
 * for the pairs that actually limit the ring (seats straddling the felt's left
 * and right extremes are far apart horizontally, so `scaleByY` is the max).
 *
 * Getting this wrong let the clamp permit a scale at which a lower seat's
 * revealed cards painted into the seat above it, which is the exact failure the
 * module exists to prevent.
 *
 * `seatCardBandReach` derives that from `getCardOffset` rather than from the
 * band-lift constants, so the footprint cannot drift from where the cards are
 * actually drawn.
 */
const seatVerticalExtent = (isPortrait: boolean) => ({
  above: seatCardBandReach(isPortrait) + SEAT_CONTAINER_HEIGHT / 2,
  below: SEAT_CONTAINER_HEIGHT / 2
});

/**
 * The largest uniform scale at which no two seats' footprints intersect.
 *
 * Two axis-aligned boxes are clear if they are separated on EITHER axis, so each
 * pair permits the larger of its two per-axis limits, and the ring permits the
 * smallest of those. Using the max-of-two-axes rather than a single radius
 * matters: seats on opposite sides of the felt are far apart horizontally and
 * near-level vertically, and a radius test would let one of them veto a scale
 * that is perfectly fine.
 *
 * Returns Infinity when nothing constrains the ring (0 or 1 seat), so callers
 * can `Math.min` it unconditionally.
 */
export const maxSeatScaleForRing = (
  seats: SeatPosition[],
  containerWidth: number,
  containerHeight: number,
  /**
   * Which orientation the SEATS paint in — the `useOrientation` value the Seat
   * components read, which decides card scales and the band lift.
   *
   * Deliberately separate from whichever orientation produced `seats`. The two
   * are not the same thing: `useEllipticalPositions` derives the RING from the
   * container's own aspect (`height > width`), while the seats inside it paint
   * from the orientation CONTEXT — and `MultiTableGrid` forces that context to
   * 'portrait' on cells whose felt is physically wider than tall. Collapsing
   * them measured a portrait footprint against a landscape ring and came out
   * ~11% under-scale in grid view.
   */
  footprintIsPortrait: boolean
): number => {
  if (seats.length < 2 || containerWidth <= 0 || containerHeight <= 0) return Infinity;

  const footprint = seatHorizontalExtent(footprintIsPortrait);
  const { above, below } = seatVerticalExtent(footprintIsPortrait);

  let limit = Infinity;
  for (let i = 0; i < seats.length; i++) {
    for (let j = i + 1; j < seats.length; j++) {
      const ax = (seats[i].leftPct / 100) * containerWidth;
      const ay = (seats[i].topPct / 100) * containerHeight;
      const bx = (seats[j].leftPct / 100) * containerWidth;
      const by = (seats[j].topPct / 100) * containerHeight;

      // The seat footprint is ASYMMETRIC — hole cards fan left on every seat
      // (EN-10520) — but the gap two neighbours need is the same either way
      // round: whichever is on the left contributes its RIGHT extent and the
      // other its LEFT. So the sum is symmetric and the ordering drops out.
      const dx = Math.abs(bx - ax);
      const dy = Math.abs(by - ay);
      const gapNeededX = horizontalGapNeeded(footprint, footprintIsPortrait);
      const gapNeededY = above + below;

      const scaleByX = gapNeededX > 0 ? dx / gapNeededX : Infinity;
      const scaleByY = gapNeededY > 0 ? dy / gapNeededY : Infinity;
      // Separated on either axis is enough.
      limit = Math.min(limit, Math.max(scaleByX, scaleByY));
    }
  }
  return limit;
};

/**
 * Every ring size the felt is sized to survive, regardless of which it renders.
 *
 * The separation clamp is measured against ALL of these and takes the tightest,
 * because sizing against the LIVE seat count gets two things wrong:
 *
 *  1. Tables side by side in a grid paint at different sizes. A three-handed
 *     table has room for enormous cards, so at "Largest" it dwarfed the 9-max
 *     tables beside it — reported from a live 4-table grid, and the reason this
 *     exists. The setting is one control; it should mean one size.
 *  2. The felt would RESIZE AS PLAYERS COME AND GO. A ninth player sitting down
 *     tightens the ring, which would shrink every card on the table mid-hand —
 *     a worse bug than the one occupancy-sizing was solving, and much harder to
 *     recognise for what it is.
 *
 * WHY THE WHOLE SET AND NOT JUST 9. "Nine is the tightest, so sizing for nine is
 * safe for everything smaller" is the obvious shortcut and it is FALSE. The ring
 * is a superellipse with arc-length-even spacing, so which pair binds moves
 * around as seats are added: measured on a 1400x700 desktop felt, 8 seats
 * permits 1.289 while 9 permits 1.317 — sizing for 9 would overlap at 8. The
 * limits are mostly monotonic in seat count, which is exactly what makes the
 * shortcut tempting.
 *
 * Taking the minimum costs little (~2% on that felt) and needs no assumption
 * about which count is worst.
 */
export const SUPPORTED_SEAT_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9];

/**
 * The smallest ring on which the tuned portrait bet COLUMNS apply.
 *
 * `PORTRAIT_FLANK_BET` was measured on rings of 6 to 9 (see its docblock), and
 * `deriveBetPosition` only routes a seat through a column when the ring is wide
 * enough to have a genuine flank. Below that the columns are not where the pill
 * goes, so treating them as a constraint measures against a pill that is not
 * there.
 */
const FLANK_COLUMN_MIN_SEATS = 6;

/**
 * The tightest scale any supported ring size permits at this container size.
 *
 * `rings` must be the ring positions for each of SUPPORTED_SEAT_COUNTS, built by
 * the caller so they can be memoised independently of the container (the ring is
 * pure percentages; only this comparison needs the measured size).
 */
export const minSeatScaleAcrossRings = (
  rings: SeatPosition[][],
  containerWidth: number,
  containerHeight: number,
  footprintIsPortrait: boolean
): number =>
  rings.reduce(
    (tightest, seats) =>
      Math.min(
        tightest,
        maxSeatScaleForRing(seats, containerWidth, containerHeight, footprintIsPortrait),
        maxSeatScaleForFlankColumns(seats, containerWidth, footprintIsPortrait)
      ),
    Infinity
  );

/**
 * The scale at which a seat card would reach a PORTRAIT flank bet column.
 *
 * Portrait flank bets do not sit at an offset from their seat — they go to a
 * fixed strip of felt (`PORTRAIT_FLANK_BET_COLUMNS_PCT`, measured off the
 * running table rather than derived). So this constraint cannot be folded into
 * the seat-to-seat gap: the pill's position does not move with the furniture,
 * which means growing the furniture walks the seats INTO it.
 *
 * Treating it as a seat-to-seat distance is what made the earlier attempts
 * oscillate — a gap wide enough to protect the columns on a phone was ~16% too
 * wide for every other pair on the ring, and a gap sized for the pairs let a
 * 390px phone at Largest put flank pills on their neighbours' cards.
 *
 * Landscape returns Infinity: there the flank bet is an offset from its own seat
 * (`dealerPlusBetX`) and scales with it, so the pairwise test already covers it.
 */
const maxSeatScaleForFlankColumns = (
  seats: SeatPosition[],
  containerWidth: number,
  isPortrait: boolean
): number => {
  // The tuned columns were measured on rings of 6 to 9 and `deriveBetPosition`
  // only routes a seat through them when the ring is wide enough to have a
  // genuine flank. On a 3-handed ring the extreme seat sits ON the column by
  // coincidence of the arc, which — charged as a constraint — drove the whole
  // felt to its floor for every table on the container.
  if (!isPortrait || seats.length < FLANK_COLUMN_MIN_SEATS || containerWidth <= 0) {
    return Infinity;
  }

  // ONLY the two EXTREME seats — the ring's leftmost and rightmost, which are
  // the flank seats these columns serve and share a height with. Every other
  // seat is higher or lower on the ring and cannot reach the pill however wide
  // it gets.
  //
  // Identified by extremity rather than by a distance-from-midline threshold:
  // a threshold has to be chosen, and any choice loose enough to catch the
  // flank seat on one ring size also catches a seat sitting a few percent from
  // a column on another, which drives the whole felt to its floor.
  const leftmost = seats.reduce((a, b) => (b.leftPct < a.leftPct ? b : a));
  const rightmost = seats.reduce((a, b) => (b.leftPct > a.leftPct ? b : a));

  // A seat card's half width plus half a pill: any closer and they touch.
  const clearance = SEAT_CARD_WIDTH / 2 + BET_PILL_WIDTH / 2;
  let limit = Infinity;
  for (const column of PORTRAIT_FLANK_BET_COLUMNS_PCT) {
    // Each column constrains only the flank seat on its own side of the felt.
    const seat = column < 50 ? leftmost : rightmost;
    const gapPx = (Math.abs(column - seat.leftPct) / 100) * containerWidth;
    limit = Math.min(limit, gapPx / clearance);
  }
  return limit;
};

/** Rounding step for the scale written to the DOM — see `resolveFeltScale`. */
export const FELT_SCALE_QUANTUM = 1 / 512;

/** Absolute floor for the painted felt scale, matching useResponsiveScale's min. */
export const FELT_SCALE_MIN = 0.5;

/**
 * The painted scale for a table: where the player's slider sits inside THIS
 * table's own safe range.
 *
 * The slider is a POSITION, not an absolute multiplier that is then clipped.
 * Clipping is what shipped first and it made the control look broken: the ring
 * limit saturates at a different point for every seat count, so in a 4-table
 * grid a 9-max table pinned at ~v1.0 and never moved again while a short-handed
 * one kept growing. Dragging the slider visibly adjusted one table out of four.
 *
 * Mapping the position onto [smallest, safeMax] fixes both halves of that:
 * every table responds across the whole travel, and Largest lands exactly on the
 * largest size that table can paint without cards, pills or seats touching —
 * which is what "Largest" ought to mean.
 *
 * The consequence worth knowing: two tables at the same setting are NOT the same
 * painted size, because they do not have the same amount of room. A 6-max table
 * is bigger than the 9-max one beside it. That is the honest answer; the
 * alternative is sizing every table on screen for the most crowded one.
 *
 * Quantised before it reaches the DOM: `useContainerSize` dedupes identical
 * measurements, but sub-pixel jitter can still produce a stream of scales
 * differing in the 4th decimal, and each one is a style write that changes
 * layout — the shape of a resize loop.
 */
export const resolveFeltScale = ({
  feltFit,
  ringScaleLimit,
  tableViewScale,
  viewScaleMin,
  viewScaleMax,
  viewScaleDefault
}: {
  /** What the container can afford, from useResponsiveScale. */
  feltFit: number;
  /** What the ring can afford, from `maxSeatScaleForRing`. */
  ringScaleLimit: number;
  /** The player's stored slider value. */
  tableViewScale: number;
  viewScaleMin: number;
  viewScaleMax: number;
  /** The shipped default, which anchors the curve — see below. */
  viewScaleDefault: number;
}): number => {
  const span = viewScaleMax - viewScaleMin;
  const position = span > 0 ? (tableViewScale - viewScaleMin) / span : 0;
  const clamped = Math.max(0, Math.min(1, position));
  const defaultPosition = span > 0 ? (viewScaleDefault - viewScaleMin) / span : 0;

  // Never above the felt's own ceiling: a heads-up table has room for enormous
  // cards, but there is no point painting past the design ceiling.
  const safeMax = Math.min(feltFit * viewScaleMax, Math.max(ringScaleLimit, FELT_SCALE_MIN));

  /**
   * THE DEFAULT IS ANCHORED TO THE CONTAINER'S NATURAL SIZE, not to a point part
   * way up an arbitrary range.
   *
   * `feltFit` is exactly what the felt painted before any of this existed, so
   * anchoring the default there makes "no smaller than it used to be" a property
   * of the curve rather than something to be checked per viewport. A plain
   * lerp from `smallest` to `safeMax` does not have that property: with the
   * default two fifths up the travel, a 470x800 grid cell resolved 0.801 against
   * a previous 0.889, and the felt came out visibly smaller than the build it
   * replaced — reported from a live grid, twice.
   *
   * So the curve is two segments that meet at the default:
   *   position 0        -> feltFit * viewScaleMin   (deliberately smaller)
   *   position DEFAULT  -> feltFit                  (exactly as before)
   *   position 1        -> safeMax                  (as large as fits)
   *
   * The anchor still yields to the ring: on a felt too tight to paint even
   * `feltFit` safely, `safeMax` is below it and becomes the anchor instead.
   */
  const anchor = Math.min(feltFit, safeMax);
  const smallest = Math.min(feltFit * viewScaleMin, anchor);

  const scale =
    clamped <= defaultPosition
      ? smallest + (anchor - smallest) * (defaultPosition > 0 ? clamped / defaultPosition : 1)
      : anchor +
        (safeMax - anchor) *
          (defaultPosition < 1 ? (clamped - defaultPosition) / (1 - defaultPosition) : 0);

  // FLOOR, not round. Rounding can land up to half a quantum ABOVE `safeMax`,
  // which would put the felt fractionally past the no-overlap limit at the top
  // of the slider — the one setting where being over it is guaranteed to show.
  // Flooring costs at most 1/512 of design size, which is invisible.
  return Math.floor(scale / FELT_SCALE_QUANTUM) * FELT_SCALE_QUANTUM;
};

/**
 * A seat's painted extent at scale 1, in design px around its anchor.
 *
 * Exported so tests and callers read the footprint from one place rather than
 * re-deriving it — the separation clamp and anything that reasons about seat
 * spacing must agree on what a seat actually occupies.
 */
export const seatFootprintAtDesignSize = (isPortrait: boolean) => ({
  ...seatHorizontalExtent(isPortrait),
  ...seatVerticalExtent(isPortrait)
});
